import { useMemo } from "react";

import * as Yup from "yup";

import { SystemDropdownCategory } from "@/api";
import {
  AppointmentFormValues,
  AppointmentMaxLength,
} from "@/common/components/appointments/shared/AppointmentForm.types";
import { isKnownPurpose } from "@/common/components/appointments/shared/AppointmentForm.utils";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { useSystemDropdownOptions } from "@/common/hooks/useSystemDropdownOptions";
import { useTranslation } from "@/translations";

export function useAppointmentFormOptions() {
  const { t } = useTranslation();

  // The Ort is an ordinary dropdown, so its values are already the labels; only the Zweck needs the
  // system list, whose stable keys the column stores.
  const { appointmentLocations: locationOptions } = useDropdownOptions();
  const purposes = useSystemDropdownOptions(SystemDropdownCategory.AppointmentPurpose);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape<{ [Property in keyof AppointmentFormValues]?: Yup.AnySchema }>({
        purposeKey: Yup.string()
          .required(
            t("common.validation.fieldRequired", {
              fieldName: t("calendar.appointmentCreation.purposeName"),
            })
          )
          .max(
            AppointmentMaxLength.purposeKey,
            t("common.validation.maxLength", { max: AppointmentMaxLength.purposeKey })
          ),
        startDate: Yup.date().required(
          t("common.validation.fieldRequired", {
            fieldName: t("calendar.appointmentCreation.startDate"),
          })
        ),
        endDate: Yup.date()
          .required(
            t("common.validation.fieldRequired", {
              fieldName: t("calendar.appointmentCreation.endDate"),
            })
          )
          .test("end-date-after-start", t("calendar.appointmentCreation.endBeforeStart"), function (endDate) {
            const { startDate } = this.parent as AppointmentFormValues;
            return endDate >= startDate;
          }),
        location: Yup.string()
          .nullable()
          .when("purposeKey", (purposeKeyArr: unknown[], schema: Yup.StringSchema<string | null | undefined>) =>
            isKnownPurpose(purposeKeyArr[0] as string | undefined, purposes)
              ? schema.required(
                  t("common.validation.fieldRequired", {
                    fieldName: t("calendar.appointmentCreation.location"),
                  })
                )
              : schema
          )
          .max(AppointmentMaxLength.location, t("common.validation.maxLength", { max: AppointmentMaxLength.location })),
        details: Yup.string().max(
          AppointmentMaxLength.details,
          t("common.validation.maxLength", { max: AppointmentMaxLength.details })
        ),
      }),
    [t, purposes]
  );

  return {
    locationOptions,
    purposeOptions: purposes.optionItems,
    purposes,
    validationSchema,
  };
}
